import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { DownloadSimple, FileText, MagnifyingGlass, Coins } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";

function currentMonth() {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
}
function money(v, cur = "INR") {
  if (v == null) return "—";
  return `${cur} ${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

export default function PayrollRegister() {
  const { user, tenantId } = useAuth();
  const [period, setPeriod] = useState(currentMonth());
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { period_month: period };
    if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
    api.get("/reports/payroll-register", { params })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, tenantId]);

  const departments = useMemo(() => Array.from(new Set((data.rows || []).map((r) => r.department).filter(Boolean))), [data.rows]);
  const filtered = useMemo(() => (data.rows || []).filter((r) => {
    const matchQ = !q || (r.employee_code + " " + r.employee_name).toLowerCase().includes(q.toLowerCase());
    const matchD = !dept || r.department === dept;
    return matchQ && matchD;
  }), [data.rows, q, dept]);

  const exportCSV = () => {
    if (filtered.length === 0) { toast.error("No rows to export"); return; }
    const cols = ["employee_code", "employee_name", "department", "present_days", "working_days", "basic", "hra", "special_allowance", "gross", "employee_pf", "employee_esic", "professional_tax", "tds", "total_deductions", "net_pay", "ctc"];
    const cq = (v) => JSON.stringify(v ?? "");
    // Prepend company header rows so the CSV is self-identifying
    const companyName = data.tenant?.name || "Company";
    const preamble = [
      `${cq(companyName)}`,
      `${cq("Payroll Register")},${cq(period)}`,
      data.tenant?.footer ? cq(data.tenant.footer) : "",
      "",
    ];
    const header = cols.join(",");
    const lines = filtered.map((r) => cols.map((c) => cq(r[c])).join(","));
    const csv = [...preamble, header, ...lines].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const safeName = companyName.replace(/[^a-zA-Z0-9-]+/g, "_");
    const a = document.createElement("a"); a.href = url; a.download = `payroll-register_${safeName}_${period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const totals = data.totals || {};

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-adb-navy font-semibold">Payroll · Register</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Payroll register</h1>
          {data.tenant?.name && <p className="text-sm text-adb-navy font-semibold mt-1" data-testid="reg-company-name">{data.tenant.name}</p>}
          <p className="text-sm text-zinc-500 mt-2">Structured export of all payslips for a month · gross · statutory · net.</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm w-40 font-mono-plex" data-testid="reg-period" />
          <Button onClick={exportCSV} variant="outline" className="rounded-sm" data-testid="reg-export-csv"><DownloadSimple size={14} className="mr-1" /> Export CSV</Button>
        </div>
      </div>

      {/* Totals bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <TotalCard label="Employees" value={totals.employees ?? 0} icon={<FileText size={14} />} />
        <TotalCard label="Gross" value={money(totals.gross)} accent />
        <TotalCard label="Deductions" value={money(totals.deductions)} muted />
        <TotalCard label="Net paid" value={money(totals.net)} accent bold />
        <TotalCard label="CTC" value={money(totals.ctc)} muted />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-zinc-400" />
          <Input placeholder="Search code or name…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm w-64" data-testid="reg-search" />
        </div>
        <select value={dept} onChange={(e) => setDept(e.target.value)} className="border border-zinc-300 rounded-sm text-sm px-2 py-1.5 bg-white" data-testid="reg-dept">
          <option value="">All departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <Badge variant="outline" className="rounded-sm">{filtered.length} rows</Badge>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-x-auto bg-white">
        <table className="hr-table" data-testid="reg-table">
          <thead>
            <tr>
              <th>Code</th><th>Name</th><th>Dept</th><th>Days</th>
              <th>Basic</th><th>HRA</th><th>Special</th><th>Gross</th>
              <th>PF</th><th>ESIC</th><th>PT</th><th>TDS</th>
              <th>Deductions</th><th>Net pay</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={14} className="text-center py-6 text-sm text-zinc-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={14} className="text-center py-8 text-sm text-zinc-500"><Coins size={20} className="inline mr-1 text-zinc-300" />No payroll run for this period yet. Run payroll first.</td></tr>}
            {!loading && filtered.map((r, i) => (
              <tr key={i} data-testid={`reg-row-${r.employee_code}`}>
                <td className="font-mono-plex text-[11px]">{r.employee_code}</td>
                <td className="text-sm">{r.employee_name}</td>
                <td className="text-xs text-zinc-500">{r.department || "—"}</td>
                <td className="font-mono-plex text-xs">{r.present_days}/{r.working_days}</td>
                <td className="font-mono-plex text-xs">{money(r.basic)}</td>
                <td className="font-mono-plex text-xs">{money(r.hra)}</td>
                <td className="font-mono-plex text-xs">{money(r.special_allowance)}</td>
                <td className="font-mono-plex text-xs font-semibold">{money(r.gross)}</td>
                <td className="font-mono-plex text-xs text-red-600">{money(r.employee_pf)}</td>
                <td className="font-mono-plex text-xs text-red-600">{money(r.employee_esic)}</td>
                <td className="font-mono-plex text-xs text-red-600">{money(r.professional_tax)}</td>
                <td className="font-mono-plex text-xs text-red-600">{money(r.tds)}</td>
                <td className="font-mono-plex text-xs text-red-700 font-semibold">{money(r.total_deductions)}</td>
                <td className="font-mono-plex text-sm font-semibold text-emerald-800">{money(r.net_pay)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TotalCard({ label, value, icon, accent, muted, bold }) {
  return (
    <div className={`border rounded-sm p-3 ${accent ? "bg-adb-navy/5 border-adb-navy/20" : muted ? "bg-zinc-50 border-zinc-200" : "bg-white border-zinc-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">{icon}{label}</div>
      <div className={`mt-1 font-mono-plex ${bold ? "text-2xl font-bold" : "text-lg"} text-zinc-900`}>{value}</div>
    </div>
  );
}
